import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, Clock3, Eye, X, AlertCircle, Building2, CalendarCheck, CheckCircle2, Printer } from 'lucide-react';
import AppLayout from '@components/AppLayout';
import '../styles/my-bookings.css';
import { API_BASE_URL } from '../../src/config';
import { resolveBackendAssetUrl } from '../../src/utils/assetUrl';
import { getMemberAuthToken, getMemberAuthUserId } from '../../src/utils/memberAuth';

const STATUS_META = {
  pending: { label: 'Pending Approval', tone: 'pending' },
  approved: { label: 'Approved', tone: 'approved' },
  rejected: { label: 'Rejected', tone: 'rejected' },
  cancelled: { label: 'Cancelled', tone: 'cancelled' },
  completed: { label: 'Completed', tone: 'completed' },
};

const PAYMENT_META = {
  paid: { label: 'Paid', tone: 'approved' },
  refunded: { label: 'Refunded', tone: 'approved' },
  pending: { label: 'Pending', tone: 'pending' },
  processing: { label: 'Processing', tone: 'pending' },
  unpaid: { label: 'Unpaid', tone: 'pending' },
  failed: { label: 'Failed', tone: 'rejected' },
  expired: { label: 'Expired', tone: 'cancelled' },
  waived: { label: 'Waived', tone: 'approved' },
};

const VENUE_PAYMENT_WINDOW_MS = 5 * 60 * 1000;

const resolveImageSrc = (value) => {
  return resolveBackendAssetUrl(value, { apiBaseUrl: API_BASE_URL });
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const formatTime = (value) => {
  if (!value) return '—';
  const [hours, minutes] = value.split(':');
  const hour = parseInt(hours, 10);
  const period = hour < 12 ? 'AM' : 'PM';
  const displayHour = hour > 12 ? hour - 12 : hour;
  return `${displayHour}:${minutes || '00'} ${period}`;
};

const formatPaymentLabel = (value) => {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (!normalized) return PAYMENT_META.unpaid;
  return PAYMENT_META[normalized] || { label: normalized, tone: 'pending' };
};

const formatPeso = (value) => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return `₱${num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatCountdown = (msLeft) => {
  const totalSeconds = Math.max(0, Math.floor(msLeft / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

const normalizeStatus = (value) => (value || '').toString().trim().toLowerCase();

const resolveEffectivePaymentStatus = (booking) => {
  const paymentStatus = normalizeStatus(booking?.paymentStatus);
  if (normalizeStatus(booking?.status) !== 'rejected') {
    return paymentStatus || 'unpaid';
  }

  const refundStatus = normalizeStatus(booking?.refundStatus);
  if (refundStatus === 'succeeded') return 'refunded';
  if (refundStatus === 'failed' || refundStatus === 'canceled' || refundStatus === 'cancelled') return 'failed';
  if (refundStatus === 'processing' || refundStatus === 'pending') return 'processing';

  if (paymentStatus === 'refunded') return 'refunded';
  if (paymentStatus === 'failed') return 'failed';
  if (paymentStatus === 'processing') return 'processing';

  return 'processing';
};

const resolveRefundAlertState = (booking) => {
  const effectivePaymentStatus = resolveEffectivePaymentStatus(booking);

  if (effectivePaymentStatus === 'failed') {
    return {
      key: 'failed',
      title: 'Booking Rejected — Refund Failed',
      note: 'There was an issue processing your refund with your bank. Please contact union support for manual resolution.',
      className: 'my-bookings__refund-alert--failed',
      showSuccessIcon: false,
    };
  }

  if (effectivePaymentStatus === 'refunded') {
    return {
      key: 'succeeded',
      title: 'Booking Rejected & Successfully Refunded',
      note: 'Your refund has been successfully processed by our system.',
      className: 'my-bookings__refund-alert--succeeded',
      showSuccessIcon: true,
    };
  }

  return {
    key: 'processing',
    title: 'Booking Rejected & Refund Processing',
    note: 'Funds usually appear in your account within 5-10 business days.',
    className: 'my-bookings__refund-alert--processing',
    showSuccessIcon: false,
  };
};

export default function MyVenueBookingsPage({ user, onLogout }) {
  const navigate = useNavigate();

  const isVerified = user?.isApproved === 'active' || user?.isApproved === 'approved' || user?.isApproved === true || user?.isApproved === 1 || user?.isApproved === '1';
  const isIncomplete = user?.isApproved === 'incomplete';
  const effectiveMemberUserId = getMemberAuthUserId() || Number(user?.id || 0) || null;

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [detailDialog, setDetailDialog] = useState({ open: false, booking: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, booking: null });
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [paymentLoadingId, setPaymentLoadingId] = useState(null);
  const [nowMs, setNowMs] = useState(Date.now());

  const firstName = (user?.firstName || user?.name?.split(' ')[0] || 'Member').toString().trim() || 'Member';
  const hasNoBookedEvents = !loading && !error && bookings.length === 0;
  const bookingsHeaderMessage = hasNoBookedEvents
    ? 'No Booked Venues Yet. Book a venue to get started.'
    : 'Here are your booked venues.';

  useEffect(() => {
    const timer = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const fetchBookings = async () => {
      if (!effectiveMemberUserId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_BASE_URL}/api/my-bookings?userId=${effectiveMemberUserId}`);
        if (!response.ok) {
          throw new Error('Failed to load bookings');
        }
        const data = await response.json();
        setBookings(data.bookings || []);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setError('Unable to load your bookings. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookings();
  }, [effectiveMemberUserId]);

  const startVenueBookingCheckout = async (booking) => {
    const token = getMemberAuthToken();
    const headers = {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };

    setPaymentLoadingId(booking.id);
    try {
      const response = await fetch(`${API_BASE_URL}/api/payments/checkout-session`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          paymentFor: 'venue_booking',
          referenceId: booking.id,
          userId: effectiveMemberUserId,
        }),
      });

      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || payload.reason || 'Unable to start secure checkout.');
      }

      if (!payload.checkoutUrl) {
        throw new Error('Secure checkout URL is missing.');
      }

      window.location.assign(payload.checkoutUrl);
    } catch (error) {
      setError(error?.message || 'Unable to start payment. Please try again.');
    } finally {
      setPaymentLoadingId(null);
    }
  };

  const openDetail = (booking) => {
    setDetailDialog({ open: true, booking });
  };

  const closeDetail = () => {
    setDetailDialog({ open: false, booking: null });
  };

  const openCancel = (booking) => {
    setCancelDialog({ open: true, booking });
    setCancelError('');
  };

  const closeCancel = () => {
    if (cancelLoading) return;
    setCancelDialog({ open: false, booking: null });
    setCancelError('');
  };

  const handlePrintReceipt = (booking) => {
    const referenceId = `VB-${String(booking.id).padStart(6, '0')}`;
    const bookingDateStr = formatDate(booking.bookingDate);
    const startTimeStr = formatTime(booking.startTime);
    const endTimeStr = formatTime(booking.endTime);
    const amountStr = formatPeso(booking.paymentAmountPhp);
    const paidAtStr = formatDate(booking.paymentPaidAt);

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>Receipt - ${referenceId}</title>
<style>
  @page { size: A4; margin: 20mm; }
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #0f172a; background: #f8fafc; }
  .receipt { max-width: 600px; margin: 0 auto; padding: 32px; background: #fff; }
  .receipt-header { text-align: center; margin-bottom: 24px; padding-bottom: 16px; border-bottom: 2px solid #2563eb; }
  .receipt-header h1 { font-size: 20px; color: #2563eb; margin-bottom: 4px; }
  .receipt-header .ref { font-size: 13px; color: #64748b; }
  .receipt-section { margin-bottom: 18px; }
  .receipt-section h3 { font-size: 12px; text-transform: uppercase; letter-spacing: 0.05em; color: #2563eb; margin-bottom: 8px; padding-bottom: 4px; border-bottom: 1px solid #e2e8f0; }
  .receipt-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 20px; }
  .receipt-item label { display: block; font-size: 11px; text-transform: uppercase; letter-spacing: 0.03em; color: #64748b; margin-bottom: 1px; }
  .receipt-item p { font-size: 14px; font-weight: 500; }
  .receipt-footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 11px; color: #64748b; }
  @media print { body { background: #fff; } .receipt { box-shadow: none; } }
</style>
</head>
<body>
<div class="receipt">
  <div class="receipt-header">
    <h1>Venue Booking Receipt</h1>
    <div class="ref">${referenceId}</div>
  </div>
  <div class="receipt-section">
    <h3>Booking Details</h3>
    <div class="receipt-grid">
      <div class="receipt-item"><label>Receipt ID</label><p>${referenceId}</p></div>
      <div class="receipt-item"><label>Booking Reference ID</label><p>#${booking.id}</p></div>
      <div class="receipt-item"><label>Venue Name</label><p>${booking.venueName || '—'}</p></div>
      <div class="receipt-item"><label>Booking Date</label><p>${bookingDateStr}</p></div>
      <div class="receipt-item"><label>Start Time</label><p>${startTimeStr}</p></div>
      <div class="receipt-item"><label>End Time</label><p>${endTimeStr}</p></div>
    </div>
  </div>
  <div class="receipt-section">
    <h3>Payment Details</h3>
    <div class="receipt-grid">
      <div class="receipt-item"><label>Amount Paid</label><p>${amountStr}</p></div>
      <div class="receipt-item"><label>Payment Method</label><p>Stripe (Online)</p></div>
      <div class="receipt-item"><label>Payment Status</label><p>Paid</p></div>
      <div class="receipt-item"><label>Payment Date</label><p>${paidAtStr}</p></div>
    </div>
  </div>
  <div class="receipt-footer">
    Generated on ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
  </div>
</div>
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},120)})</script>
</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const iframeDoc = iframe.contentWindow?.document;
    if (!iframeDoc) {
      document.body.removeChild(iframe);
      return;
    }

    iframeDoc.open();
    iframeDoc.write(html);
    iframeDoc.close();

    const triggerPrint = () => {
      setTimeout(() => {
        try {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        } catch (err) {
          console.error('Print failed:', err);
        } finally {
          setTimeout(() => {
            if (iframe.parentNode) document.body.removeChild(iframe);
          }, 1000);
        }
      }, 250);
    };

    if (iframe.contentWindow?.document?.readyState === 'complete') {
      triggerPrint();
      return;
    }
    iframe.onload = triggerPrint;
  };

  const confirmCancel = async () => {
    if (!user?.id || !cancelDialog.booking) return;

    setCancelLoading(true);
    setCancelError('');
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/my-bookings/${cancelDialog.booking.id}/cancel`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: user.id }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to cancel booking');
      }

      setBookings((prev) =>
        prev.map((b) => (b.id === cancelDialog.booking.id ? { ...b, status: 'cancelled' } : b))
      );
      closeCancel();
    } catch (err) {
      console.error('Error cancelling booking:', err);
      setCancelError(err.message || 'Unable to cancel booking. Please try again.');
    } finally {
      setCancelLoading(false);
    }
  };

  const renderRejectedRefundAlert = (booking, isModal = false) => {
    const alertState = resolveRefundAlertState(booking);
    return (
      <div
        className={`my-bookings__refund-alert ${alertState.className}${isModal ? ' my-bookings__refund-alert--modal' : ''}`}
        role="alert"
        aria-live="polite"
      >
        <h4 className={`my-bookings__refund-alert-title${alertState.showSuccessIcon ? ' my-bookings__refund-alert-title--with-icon' : ''}`}>
          {alertState.showSuccessIcon ? <CheckCircle2 size={16} aria-hidden="true" /> : null}
          {alertState.title}
        </h4>
        <p className="my-bookings__refund-alert-line">
          <strong>Reason:</strong> {booking.rejectionReason || 'No reason provided'}
        </p>
        <p className="my-bookings__refund-alert-line">
          <strong>Amount:</strong> {formatPeso(booking.refundAmountPhp)}
        </p>
        <p className="my-bookings__refund-alert-note">{alertState.note}</p>
      </div>
    );
  };

  if (!isVerified) {
    return (
      <AppLayout title="Booked Venues" user={user} onLogout={onLogout}>
        <div className="my-bookings-page">
          <header className="my-bookings__header">
            <p className="my-bookings__header-message">
              <strong>Hi, {firstName}.</strong> {bookingsHeaderMessage}
            </p>
          </header>

          <div className="verification-prompt" style={{
            padding: '4rem 2rem',
            textAlign: 'center',
            maxWidth: '600px',
            margin: '2rem auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ color: '#f59e0b' }}>
              <AlertCircle size={48} />
            </div>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#1f2937' }}>
              {isIncomplete ? "Verification Required" : "Application Under Review"}
            </h2>
            <p style={{ color: '#4b5563', lineHeight: '1.6' }}>
              {isIncomplete
                ? "Your membership profile is incomplete. Please verify your details to access venue booking."
                : "Your membership application is still under review. Once approved, you will gain access to venue booking."}
            </p>
            <div style={{ display: 'flex', gap: '1rem' }}>
              {isIncomplete ? (
                <button
                  type="button"
                  onClick={() => navigate('/complete-profile')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  Complete Verification
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate('/membership-form')}
                  style={{
                    backgroundColor: '#2563eb',
                    color: 'white',
                    padding: '0.75rem 1.5rem',
                    borderRadius: '0.375rem',
                    border: 'none',
                    fontWeight: '500',
                    cursor: 'pointer'
                  }}
                >
                  View Application
                </button>
              )}
              <button
                type="button"
                onClick={() => navigate('/dashboard')}
                style={{
                  backgroundColor: 'white',
                  color: '#374151',
                  padding: '0.75rem 1.5rem',
                  borderRadius: '0.375rem',
                  border: '1px solid #d1d5db',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title="Booked Venues" user={user} onLogout={onLogout}>
      <div className="my-bookings-page">
        <div className="my-bookings__toolbar">
          <header className="my-bookings__header">
            <p className="my-bookings__header-message">
              <strong>Hi, {firstName}.</strong> {bookingsHeaderMessage}
            </p>
          </header>

          <button
            type="button"
            className="my-bookings__action"
            onClick={() => navigate('/venues')}
          >
            Book a Venue
          </button>
        </div>

        {loading && (
          <div className="my-bookings__state">
            <div className="my-bookings__spinner" />
            <p>Loading your bookings...</p>
          </div>
        )}

        {error && !loading && (
          <div className="my-bookings__state my-bookings__state--error">
            <AlertCircle size={24} />
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && bookings.length === 0 && (
          <div className="my-bookings__empty">
            <div className="my-bookings__empty-icon">
              <Building2 size={32} />
            </div>
            <h3>No Bookings Yet</h3>
            <p>You haven't made any venue bookings yet. Start by browsing available venues.</p>
            <button
              type="button"
              className="my-bookings__action"
              onClick={() => navigate('/venues')}
            >
              Browse Venues
            </button>
          </div>
        )}

        {!loading && !error && bookings.length > 0 && (
          <div className="my-bookings__list">
            {bookings.map((booking) => {
              const statusMeta = STATUS_META[booking.status] || STATUS_META.pending;
              const effectivePaymentStatus = resolveEffectivePaymentStatus(booking);
              const isPaymentSettled = ['paid', 'waived', 'refunded'].includes(effectivePaymentStatus);
              const createdAtMs = new Date(booking.createdAt).getTime();
              const expiresAtMs = Number.isFinite(createdAtMs) ? createdAtMs + VENUE_PAYMENT_WINDOW_MS : null;
              const msLeft = Number.isFinite(expiresAtMs) ? expiresAtMs - nowMs : 0;
              const isPaymentWindowExpired = booking.status === 'pending' && msLeft <= 0 && !isPaymentSettled;
              const canCancel = booking.status === 'pending' && !isPaymentWindowExpired;
              const canPay = booking.status === 'pending' && !isPaymentWindowExpired && !isPaymentSettled;
              const showRejectedRefundAlert = booking.status === 'rejected';
              const displayStatusMeta = isPaymentWindowExpired
                ? { label: 'Expired', tone: 'cancelled' }
                : statusMeta;

              return (
                <article key={booking.id} className="my-bookings__card">
                  <div className="my-bookings__card-image">
                    {booking.venueImage ? (
                      <img src={resolveImageSrc(booking.venueImage)} alt={booking.venueName} />
                    ) : (
                      <div className="my-bookings__card-placeholder">
                        <Building2 size={32} />
                      </div>
                    )}
                  </div>

                  <div className="my-bookings__card-content">
                    <div className="my-bookings__card-header">
                      <div className="my-bookings__card-title-row">
                        <h3 className="my-bookings__card-title">{booking.venueName}</h3>
                        <span className={`my-bookings__badge my-bookings__badge--${displayStatusMeta.tone}`}>
                          {displayStatusMeta.label}
                        </span>
                      </div>
                    </div>

                    <div className="my-bookings__schedule-pills" aria-label="Booking schedule">
                      <span className="my-bookings__schedule-pill">
                        <CalendarDays size={15} aria-hidden="true" />
                        {formatDate(booking.bookingDate)}
                      </span>

                      <span className="my-bookings__schedule-pill">
                        <Clock3 size={15} aria-hidden="true" />
                        {`${formatTime(booking.startTime)} - ${formatTime(booking.endTime)}`}
                      </span>
                    </div>

                    {showRejectedRefundAlert && (
                      renderRejectedRefundAlert(booking)
                    )}

                    <div className="my-bookings__card-actions">
                      <button
                        type="button"
                        className="my-bookings__button my-bookings__button--dark"
                        onClick={() => openDetail(booking)}
                      >
                        View Booking Details
                      </button>
                      {canPay && (
                        <button
                          type="button"
                          className="my-bookings__button my-bookings__button--primary"
                          onClick={() => startVenueBookingCheckout(booking)}
                          disabled={paymentLoadingId === booking.id}
                        >
                          {paymentLoadingId === booking.id ? 'Redirecting...' : 'Pay Now'}
                        </button>
                      )}
                      {canCancel && (
                        <button
                          type="button"
                          className="my-bookings__button my-bookings__button--danger"
                          onClick={() => openCancel(booking)}
                        >
                          Cancel Booking
                        </button>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {/* Detail Dialog */}
        {detailDialog.open && detailDialog.booking && (
          <div className="my-bookings__modal" role="dialog" aria-modal="true">
            <button
              type="button"
              className="my-bookings__modal-backdrop"
              onClick={closeDetail}
              aria-label="Close booking details dialog"
            />
            <div className="my-bookings__modal-panel">
              <header className="my-bookings__modal-header">
                <div className="my-bookings__modal-title-wrap">
                  <h3>Booking Details</h3>
                  <p className="my-bookings__modal-subtitle">Review your booking timeline, payment, and receipt details.</p>
                </div>
                <button
                  type="button"
                  onClick={closeDetail}
                  className="my-bookings__icon-button"
                  aria-label="Close dialog"
                >
                  <X size={20} />
                </button>
              </header>

              <div className="my-bookings__modal-body">
                <div className="my-bookings__detail-head">
                  <div className="my-bookings__detail-head-item">
                    <span className="my-bookings__detail-head-label">Booking ID</span>
                    <span className="my-bookings__detail-head-value">#{detailDialog.booking.id}</span>
                  </div>
                  <div className="my-bookings__detail-head-item">
                    <span className="my-bookings__detail-head-label">Payment</span>
                    <span className="my-bookings__detail-head-value">
                      {(() => {
                        const effectivePaymentStatus = resolveEffectivePaymentStatus(detailDialog.booking);
                        const paymentMeta = formatPaymentLabel(effectivePaymentStatus);
                        return <span className={`my-bookings__badge my-bookings__badge--${paymentMeta.tone}`}>{paymentMeta.label}</span>;
                      })()}
                    </span>
                  </div>
                  <div className="my-bookings__detail-head-item">
                    <span className="my-bookings__detail-head-label">Contact</span>
                    <span className="my-bookings__detail-head-value">
                      {detailDialog.booking.customerName}
                      {detailDialog.booking.mobileNumber ? ` • ${detailDialog.booking.mobileNumber}` : ''}
                      {!detailDialog.booking.mobileNumber && detailDialog.booking.email ? ` • ${detailDialog.booking.email}` : ''}
                    </span>
                  </div>
                </div>

                <div className="my-bookings__detail-sections">
                  <section className="my-bookings__detail-section">
                    <h4 className="my-bookings__detail-title">Booking</h4>
                    <div className="my-bookings__detail-list">
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Status</span>
                        <span className={`my-bookings__badge my-bookings__badge--${(STATUS_META[detailDialog.booking.status] || STATUS_META.pending).tone}`}>
                          {(STATUS_META[detailDialog.booking.status] || STATUS_META.pending).label}
                        </span>
                      </div>
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Payment Window</span>
                        <span className="my-bookings__detail-value">
                          {(() => {
                            const createdAtMs = new Date(detailDialog.booking.createdAt).getTime();
                            const expiresAtMs = Number.isFinite(createdAtMs) ? createdAtMs + VENUE_PAYMENT_WINDOW_MS : null;
                            const msLeft = Number.isFinite(expiresAtMs) ? expiresAtMs - nowMs : 0;
                            const paymentStatusNormalized = (detailDialog.booking.paymentStatus || '').toString().trim().toLowerCase();
                            const isPaymentSettled = ['paid', 'waived'].includes(paymentStatusNormalized);
                            const expired = detailDialog.booking.status === 'pending' && msLeft <= 0 && !isPaymentSettled;
                            if (detailDialog.booking.status !== 'pending' || isPaymentSettled) return 'Closed';
                            return expired ? 'Expired' : `Expires in ${formatCountdown(msLeft)}`;
                          })()}
                        </span>
                      </div>
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Venue</span>
                        <span className="my-bookings__detail-value">{detailDialog.booking.venueName}</span>
                      </div>
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Venue Type</span>
                        <span className="my-bookings__detail-value">{detailDialog.booking.venueType}</span>
                      </div>
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Package</span>
                        <span className="my-bookings__detail-value">
                          {detailDialog.booking.pricingPackageLabel || '—'}
                        </span>
                      </div>
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Booking Date</span>
                        <span className="my-bookings__detail-value">{formatDate(detailDialog.booking.bookingDate)}</span>
                      </div>
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Time Range</span>
                        <span className="my-bookings__detail-value">
                          {formatTime(detailDialog.booking.startTime)} - {formatTime(detailDialog.booking.endTime)}
                        </span>
                      </div>
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Created At</span>
                        <span className="my-bookings__detail-value">{formatDate(detailDialog.booking.createdAt)}</span>
                      </div>
                    </div>
                  </section>

                  <section className="my-bookings__detail-section">
                    <h4 className="my-bookings__detail-title">Payment</h4>
                    <div className="my-bookings__detail-list">
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Final Price</span>
                        <span className="my-bookings__detail-value">
                          {formatPeso(detailDialog.booking.paymentAmountPhp ?? detailDialog.booking.pricingFinalPricePhp)}
                        </span>
                      </div>
                      {Number(detailDialog.booking.pricingDiscountPercentage || 0) > 0 && Number(detailDialog.booking.pricingOriginalPricePhp || 0) > 0 && (
                        <div className="my-bookings__detail-row">
                          <span className="my-bookings__detail-label">Original Price</span>
                          <span className="my-bookings__detail-value">{formatPeso(detailDialog.booking.pricingOriginalPricePhp)}</span>
                        </div>
                      )}
                      <div className="my-bookings__detail-row">
                        <span className="my-bookings__detail-label">Paid At</span>
                        <span className="my-bookings__detail-value">{formatDate(detailDialog.booking.paymentPaidAt)}</span>
                      </div>
                    </div>

                    {detailDialog.booking.status === 'rejected' ? renderRejectedRefundAlert(detailDialog.booking, true) : null}

                    {(() => {
                      const effectivePs = resolveEffectivePaymentStatus(detailDialog.booking);
                      if (effectivePs !== 'paid') return null;
                      const receiptRef = `VB-${String(detailDialog.booking.id).padStart(6, '0')}`;
                      return (
                        <div className="my-bookings__receipt">
                          <h4 className="my-bookings__receipt-title">Payment Receipt</h4>
                          <div className="my-bookings__receipt-grid">
                            <div><span>Receipt ID:</span> <strong>{receiptRef}</strong></div>
                            <div><span>Booking Ref:</span> <strong>#{detailDialog.booking.id}</strong></div>
                            <div><span>Venue:</span> <strong>{detailDialog.booking.venueName}</strong></div>
                            <div><span>Date:</span> <strong>{formatDate(detailDialog.booking.bookingDate)}</strong></div>
                            <div><span>Time:</span> <strong>{formatTime(detailDialog.booking.startTime)} - {formatTime(detailDialog.booking.endTime)}</strong></div>
                            <div><span>Amount:</span> <strong>{formatPeso(detailDialog.booking.paymentAmountPhp)}</strong></div>
                            <div><span>Method:</span> <strong>Stripe</strong></div>
                            <div><span>Status:</span> <strong className="my-bookings__receipt-paid">Paid</strong></div>
                            <div><span>Paid At:</span> <strong>{formatDate(detailDialog.booking.paymentPaidAt)}</strong></div>
                          </div>
                          {(detailDialog.booking.receiptUrl || detailDialog.booking.invoiceUrl) && (
                            <a
                              href={detailDialog.booking.receiptUrl || detailDialog.booking.invoiceUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="my-bookings__receipt-link"
                            >
                              View Official Stripe Receipt
                            </a>
                          )}
                          <button
                            type="button"
                            className="my-bookings__button my-bookings__button--secondary my-bookings__receipt-print"
                            onClick={() => handlePrintReceipt(detailDialog.booking)}
                          >
                            <Printer size={16} />
                            Print Receipt
                          </button>
                        </div>
                      );
                    })()}
                  </section>

                </div>
              </div>

              <footer className="my-bookings__modal-footer">
                <button
                  type="button"
                  className="my-bookings__button my-bookings__button--primary my-bookings__modal-btn"
                  onClick={closeDetail}
                >
                  Close
                </button>
                {detailDialog.booking.status === 'pending' && (
                  <button
                    type="button"
                    className="my-bookings__button my-bookings__button--danger my-bookings__modal-btn"
                    onClick={() => {
                      closeDetail();
                      openCancel(detailDialog.booking);
                    }}
                  >
                    Cancel Booking
                  </button>
                )}
                {(() => {
                  const createdAtMs = new Date(detailDialog.booking.createdAt).getTime();
                  const expiresAtMs = Number.isFinite(createdAtMs) ? createdAtMs + VENUE_PAYMENT_WINDOW_MS : null;
                  const msLeft = Number.isFinite(expiresAtMs) ? expiresAtMs - nowMs : 0;
                  const effectivePaymentStatus = resolveEffectivePaymentStatus(detailDialog.booking);
                  const isPaymentSettled = ['paid', 'waived', 'refunded'].includes(effectivePaymentStatus);
                  const canPay = detailDialog.booking.status === 'pending' && msLeft > 0 && !isPaymentSettled;

                  if (!canPay) return null;
                  return (
                    <button
                      type="button"
                      className="my-bookings__button my-bookings__button--primary my-bookings__modal-btn"
                      onClick={() => startVenueBookingCheckout(detailDialog.booking)}
                      disabled={paymentLoadingId === detailDialog.booking.id}
                    >
                      {paymentLoadingId === detailDialog.booking.id ? 'Redirecting...' : 'Pay Now'}
                    </button>
                  );
                })()}
              </footer>
            </div>
          </div>
        )}

        {/* Cancel Confirmation Dialog */}
        {cancelDialog.open && cancelDialog.booking && (
          <div className="my-bookings__modal" role="dialog" aria-modal="true">
            <button
              type="button"
              className="my-bookings__modal-backdrop"
              onClick={closeCancel}
              aria-label="Close cancel dialog"
            />
            <div className="my-bookings__modal-panel my-bookings__modal-panel--sm">
              <header className="my-bookings__modal-header">
                <h3>Cancel Booking?</h3>
                <button
                  type="button"
                  onClick={closeCancel}
                  className="my-bookings__icon-button"
                  aria-label="Close dialog"
                >
                  <X size={20} />
                </button>
              </header>

              <div className="my-bookings__modal-body">
                <p className="my-bookings__modal-text">
                  Are you sure you want to cancel your booking for{' '}
                  <strong>{cancelDialog.booking.venueName}</strong> on{' '}
                  <strong>{formatDate(cancelDialog.booking.bookingDate)}</strong>?
                </p>
                <p className="my-bookings__modal-subtext">
                  This action cannot be undone. You will need to create a new booking request if you
                  change your mind.
                </p>
                {cancelError && (
                  <div className="my-bookings__error-message">
                    <AlertCircle size={18} />
                    {cancelError}
                  </div>
                )}
              </div>

              <footer className="my-bookings__modal-footer">
                <button
                  type="button"
                  className="my-bookings__button my-bookings__button--secondary"
                  onClick={closeCancel}
                  disabled={cancelLoading}
                >
                  Keep Booking
                </button>
                <button
                  type="button"
                  className="my-bookings__button my-bookings__button--danger"
                  onClick={confirmCancel}
                  disabled={cancelLoading}
                >
                  {cancelLoading ? 'Cancelling...' : 'Confirm Cancellation'}
                </button>
              </footer>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

MyVenueBookingsPage.propTypes = {
  user: PropTypes.object,
  onLogout: PropTypes.func.isRequired,
};
